'use client';

import { FC } from 'react';
import Image from 'next/image';
import FadeIn from '@/components/FadeIn/FadeIn';
import {
  ProductHero,
  TrustBar,
  ProductFAQ,
  ProductCTAFooter,
} from '../components';

// Feature card component
const FeatureCard: FC<{
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  compactImage?: boolean; // Reduce bottom padding for cards with extra whitespace in image
}> = ({ title, description, image, imageAlt, compactImage }) => (
  <div
    className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 lg:p-10"
    style={compactImage ? { paddingBottom: '24px' } : undefined}
  >
    <div className="mb-6">
      <h3
        className="font-medium text-black mb-3"
        style={{ fontSize: 'clamp(1.25rem, 2vw, 2rem)' }}
      >
        {title}
      </h3>
      <p
        className="text-slate-600"
        style={{ fontSize: 'clamp(1rem, 1.25vw, 1.25rem)' }}
      >
        {description}
      </p>
    </div>
    <div className="rounded-xl overflow-hidden bg-white shadow-sm">
      <Image
        src={image}
        alt={imageAlt}
        width={1200}
        height={600}
        className="w-full h-auto object-contain"
      />
    </div>
  </div>
);

const DIALER_FEATURES = {
  dontCallCold: {
    title: "Don't call cold. Call warmed accounts",
    description: 'MarketBetter sequences the warm-up across channels, then puts the best calls in your queue.',
    image: '/images/products/cold-calls/dont-call-cold.svg',
    imageAlt: 'Warmed accounts queue interface',
  },
  everyCallEnds: {
    title: 'Every call ends with clear next steps',
    description: 'MarketBetter scores calls and extracts summaries—so teams move faster and coach with proof.',
    image: '/images/products/cold-calls/every-call-ends.svg',
    imageAlt: 'Call next steps interface',
  },
  everyCallCaptured: {
    title: 'Every call is captured & measurable',
    description: 'We summarize calls, log outcomes to rep performance for better coaching and connect rates.',
    image: '/images/products/cold-calls/every-call-captured.svg',
    imageAlt: 'Call metrics dashboard',
  },
  makeCallsSalesforce: {
    title: 'Make calls where reps already work: Salesforce',
    description: 'Click-to-dial from Leads/Contacts, keep context, and log outcomes back automatically.',
    image: '/images/products/cold-calls/make-calls-salesforce.svg',
    imageAlt: 'Salesforce integration interface',
  },
};

const DIALER_FAQS = [
  {
    question: 'How do you decide who shows up in the call queue?',
    answer: 'Our AI prioritizes leads based on engagement signals, sequence stage, and timing. Warmer leads who have interacted with emails or your website surface first.',
  },
  {
    question: 'Does our brand show on caller ID?',
    answer: 'Yes! You can configure local presence dialing and branded caller ID to increase pickup rates and build trust with prospects.',
  },
  {
    question: 'Can reps call from Salesforce?',
    answer: 'Absolutely. Our Salesforce integration enables click-to-dial directly from Lead and Contact records with full context and automatic activity logging.',
  },
  {
    question: 'Do calls create follow-ups automatically?',
    answer: 'Yes, based on call outcomes and AI analysis, the system can automatically create follow-up tasks, send email sequences, or schedule callbacks.',
  },
];

const ColdCallsPage: FC = () => {
  return (
    <div className="bg-white">
      <ProductHero
        iconSrc="/images/products/icon-dialer.svg"
        label="Dialer"
        title="A dialer built for warm outbound"
        highlightedWord="warm"
        subtitle="Make smarter calls with a browser dialer connected to your plays—scripts, objections, and next steps included."
        primaryCTA={{
          label: 'Book a demo',
          href: '/book-demo',
        }}
      // Temporarily hidden
      // secondaryCTA={{
      //   label: 'Dialer tour',
      //   href: '#dialer-tour',
      // }}
      />

      <TrustBar />

      {/* Custom Feature Section with specific layout */}
      <section className="py-16 md:py-24">
        <div className="container-fluid px-4 md:px-7.5 mx-auto" style={{ maxWidth: 'clamp(1366px, 85vw, 1800px)' }}>
          {/* Section Title */}
          <FadeIn variant="up" delay={0.1}>
            <div className="text-center mb-12">
              <h2
                className="font-medium text-black"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 3rem)' }}
              >
                All <span style={{ color: '#094FFE' }}>Call</span> queues from engagement
              </h2>
            </div>
          </FadeIn>

          {/* Features with custom layout */}
          <div className="space-y-6 md:space-y-8">
            {/* Row 1: Full width - Don't call cold */}
            <FadeIn variant="up" delay={0.15}>
              <FeatureCard {...DIALER_FEATURES.dontCallCold} />
            </FadeIn>

            {/* Row 2: Two columns - Every call ends + Every call captured */}
            <FadeIn variant="up" delay={0.2}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start">
                <FeatureCard {...DIALER_FEATURES.everyCallEnds} compactImage />
                <FeatureCard {...DIALER_FEATURES.everyCallCaptured} />
              </div>
            </FadeIn>

            {/* Row 3: Full width - Make calls Salesforce */}
            <FadeIn variant="up" delay={0.25}>
              <FeatureCard {...DIALER_FEATURES.makeCallsSalesforce} />
            </FadeIn>
          </div>
        </div>
      </section>

      <ProductFAQ faqs={DIALER_FAQS} />

      <ProductCTAFooter
        productName="Dialer"
        title="Smarter sales. Faster results."
        subtitle="Powered by marketbetter"
      />
    </div>
  );
};

export default ColdCallsPage;
